import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useAccounts } from '../../context/AccountContext';

// Hooks (copied from V9)
import { useDateRange } from './hooks/useDateRange';
import { useLiveData } from './hooks/useLiveData';
import { useGoogleAds } from './hooks/useGoogleAds';
import { useMetaAds } from './hooks/useMetaAds';
import { useGA4 } from './hooks/useGA4';
import { useFunnel } from './hooks/useFunnel';
import { useNotifications } from './hooks/useNotifications';
import { useEcommerce } from './hooks/useEcommerce';
import { useMonthlyPerformance } from './hooks/useMonthlyPerformance';
import { useDashboardPrefs } from './hooks/useDashboardPrefs';

// V10 Components
import { CommandBar } from './components/CommandBar';
import { LivePulseStrip } from './components/LivePulseStrip';
import { HeroMetrics } from './components/HeroMetrics';
import { PerformanceCanvas } from './components/PerformanceCanvas';
import { PlatformSummary } from './components/PlatformSummary';
import { DailyPerformanceTable } from './components/DailyPerformanceTable';
import { EcommercePlatforms } from './components/EcommercePlatforms';
import { StoreFunnel } from './components/StoreFunnel';
import { TrafficOverview } from './components/TrafficOverview';
import { EcommerceAnalytics } from './components/EcommerceAnalytics';
import { HistoricalData } from './components/historical/HistoricalData';
import { NotificationDrawer } from './components/NotificationDrawer';

// Utils
import { ALLOWED_BRAND_IDS_FOR_CATEGORY_TABLE } from './utils/constants';

// External
import GlowyAIChatWidget from '../../components/GlowyAIChatWidget';

export default function OverviewV10() {
  const { user, selectedBrand } = useAuth();
  const { getSelectedAccountId } = useAccounts();
  const brandId = selectedBrand?.id || localStorage.getItem('currentBrandId');
  const googleAdsAccountId = getSelectedAccountId('GOOGLE_ADS');
  const isMarketer = selectedBrand?.currentUserRole === 'MARKETER';

  // --- UI state ---
  const [activeMetric, setActiveMetric] = useState('totalSales');

  // --- Date range ---
  const {
    dateRange, startDate, endDate, isCustomDateOpen,
    setDateRange, setCustomRange, setIsCustomDateOpen,
    getComparisonWindows,
  } = useDateRange();

  // --- Dashboard preferences ---
  const { pinnedMetrics, togglePin } = useDashboardPrefs();

  // --- Data hooks ---
  const liveData = useLiveData(brandId);
  const googleAds = useGoogleAds(brandId, dateRange, startDate, endDate, googleAdsAccountId);
  const metaAds = useMetaAds(brandId, dateRange, startDate, endDate);
  const ga4 = useGA4(brandId, dateRange, startDate, endDate, getComparisonWindows);
  const funnel = useFunnel(brandId, dateRange, startDate, endDate);
  const notifications = useNotifications(brandId, dateRange);
  const ecommerce = useEcommerce(
    brandId, dateRange, startDate, endDate,
    {
      hasIkasConnected: liveData.hasIkasConnected,
      hasShopifyConnected: liveData.hasShopifyConnected,
      hasWooConnected: liveData.hasWooConnected,
    },
    getComparisonWindows,
  );
  const monthly = useMonthlyPerformance(brandId, dateRange, startDate, endDate);

  // --- Render ---
  return (
    <div className="overview5-bg p-4 sm:p-6 lg:p-8" id="main-content">

      {/* Section 0: CommandBar (sticky top) */}
      <CommandBar
        user={user}
        logoUrl={selectedBrand?.logoUrl}
        brandName={selectedBrand?.name}
        dateRange={dateRange}
        startDate={startDate}
        endDate={endDate}
        isCustomDateOpen={isCustomDateOpen}
        unreadCount={notifications.unreadCount}
        updatedAt={liveData.updatedAt}
        onDateRangeChange={setDateRange}
        onCustomDateChange={setCustomRange}
        onToggleCustomDate={setIsCustomDateOpen}
        onNotificationToggle={notifications.toggleModal}
        onRefresh={liveData.refetch}
        isRefreshing={liveData.isLoading}
      />

      {/* Section 1: LivePulseStrip */}
      <LivePulseStrip
        liveGads={liveData.liveGads}
        liveMeta={liveData.liveMeta}
        liveGa4={liveData.liveGa4}
        liveIkas={liveData.liveIkas}
        liveShopify={liveData.liveShopify}
        liveWoo={liveData.liveWoo}
        hasIkasConnected={liveData.hasIkasConnected}
        hasShopifyConnected={liveData.hasShopifyConnected}
        hasWooConnected={liveData.hasWooConnected}
        isLoading={liveData.isLoading}
        updatedAt={liveData.updatedAt}
        onRefresh={liveData.refetch}
      />

      {/* Section 2: HeroMetrics (5 blended KPI with sparklines + pin) */}
      <HeroMetrics
        comparisonAggregates={ga4.comparisonAggregates}
        trendPercentages={ga4.trendPercentages}
        googleAdsSummary={googleAds.googleAdsSummary}
        metaAdsSummary={metaAds.metaAdsSummary}
        criteoAdsSummary={monthly.criteoAdsSummary}
        googleAdsUnifiedData={googleAds.unifiedData}
        metaAdsUnifiedData={metaAds.unifiedData}
        pinnedMetrics={pinnedMetrics}
        onTogglePin={togglePin}
        isLoading={ga4.isLoading}
      />

      {/* Section 3: PerformanceCanvas (chart + platform bars + device split) */}
      <PerformanceCanvas
        activeMetric={activeMetric}
        onMetricChange={setActiveMetric}
        generateChartData={ga4.generateGA4ChartData}
        googleAdsSummary={googleAds.googleAdsSummary}
        metaAdsSummary={metaAds.metaAdsSummary}
        criteoAdsSummary={monthly.criteoAdsSummary}
        gaDevicePerf={ga4.gaDevicePerf}
        comparisonAggregates={ga4.comparisonAggregates}
      />

      {/* Section 4: PlatformSummary (platform overview cards) */}
      <PlatformSummary
        googleAdsSummary={googleAds.googleAdsSummary}
        metaAdsSummary={metaAds.metaAdsSummary}
        criteoAdsSummary={monthly.criteoAdsSummary}
        comparisonAggregates={ga4.comparisonAggregates}
        isLoading={googleAds.isLoading || metaAds.isLoading}
      />

      {/* Section 5: DailyPerformanceTable */}
      <DailyPerformanceTable
        googleAdsUnifiedData={googleAds.unifiedData}
        metaAdsUnifiedData={metaAds.unifiedData}
        gaDailyTotalsMap={ga4.gaDailyTotalsMap}
        isLoading={googleAds.isLoading || metaAds.isLoading}
      />

      {/* Section 6: EcommercePlatforms (conditional) */}
      <EcommercePlatforms
        ikasSummary={ecommerce.ikasSummary}
        ikasDailyRows={ecommerce.ikasDailyRows}
        shopifySummary={ecommerce.shopifySummary}
        shopifyDailyRows={ecommerce.shopifyDailyRows}
        wooSummary={ecommerce.wooSummary}
        wooDailyRows={ecommerce.wooDailyRows}
        adjustSummary={ecommerce.adjustSummary}
        adjustDailyRows={ecommerce.adjustDailyRows}
        hasIkasConnected={liveData.hasIkasConnected}
        hasShopifyConnected={liveData.hasShopifyConnected}
        hasWooConnected={liveData.hasWooConnected}
        hasAdjustConnected={ecommerce.hasAdjustConnected}
        isLoading={ecommerce.isLoading}
      />

      {/* Section 7: StoreFunnel */}
      <StoreFunnel
        funnelData={funnel.funnelData}
        isLoading={funnel.isLoading}
        isMarketer={isMarketer}
      />

      {/* Section 8: TrafficOverview */}
      <TrafficOverview
        trafficSourcesAnalytics={ga4.trafficSourcesAnalytics}
        isMarketer={isMarketer}
      />

      {/* Section 9: EcommerceAnalytics (product/category) */}
      <EcommerceAnalytics
        topProducts={ecommerce.topProducts}
        categoryPerformance={ecommerce.categoryPerformance}
        dailyProductData={ecommerce.dailyProductData}
        isMarketer={isMarketer}
        isLoading={ecommerce.isLoading?.products}
      />

      {/* Section 10: HistoricalData (collapsible accordions) */}
      <HistoricalData
        monthlyPerformanceData={monthly.monthlyPerformanceData}
        monthlyPlatformCategoryData={monthly.monthlyPlatformCategoryData}
        channelMonthlyRevenue={monthly.channelMonthlyRevenue}
        gaMonthlySummary={monthly.gaMonthlySummary}
        weeklyRevenueByWeekday={monthly.weeklyRevenueByWeekday}
        weeklyWoWSummary={monthly.weeklyWoWSummary}
        allCostsByMonth={monthly.allCostsByMonth}
        totalAllCosts={monthly.totalAllCosts}
        weeklyMetric={monthly.weeklyMetric}
        onWeeklyMetricChange={monthly.setWeeklyMetric}
        isVisible={ALLOWED_BRAND_IDS_FOR_CATEGORY_TABLE.has(selectedBrand?.id)}
      />

      {/* Section 11: NotificationDrawer (right slide) */}
      <NotificationDrawer
        notifications={notifications.notifications}
        isOpen={notifications.isNotificationOpen}
        onClose={notifications.toggleModal}
        onMarkRead={notifications.markAsRead}
        onMarkAllRead={notifications.markAllAsRead}
        onDelete={notifications.deleteNotification}
        unreadCount={notifications.unreadCount}
        isLoading={notifications.isLoading}
        onCreateDaily={notifications.createDailyComparison}
        onCreateWeekly={notifications.createWeeklyComparison}
        onCreateMonthly={notifications.createMonthlyComparison}
      />

      {/* AI Chat FAB */}
      <GlowyAIChatWidget />
    </div>
  );
}
